import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useForecastPaginatedTableData } from 'src/features/forecastPaginated/forecastPaginatedTable/hooks/useForecastPaginatedTableData';
import { cn } from 'src/lib/utils';
import { useBoundStore } from 'src/store/store';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

type ForecastPaginatedTableProps = {
  sessionId: string;
  sku: string;
  data: ForecastDataPoint[];
  editable: boolean;
};

export function ForecastPaginatedTable({
  sessionId,
  sku,
  data,
  editable,
}: ForecastPaginatedTableProps) {
  const updateForecastProposalInStore = useBoundStore(
    (state) => state.updateForecastProposal,
  );
  const updateForecastProposal = React.useCallback(
    (currentSku: string, monthId: string, value: number) => {
      updateForecastProposalInStore(currentSku, monthId, value, sessionId);
    },
    [sessionId, updateForecastProposalInStore],
  );

  const { tableData, columns } = useForecastPaginatedTableData(
    data,
    sku,
    updateForecastProposal,
    editable,
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table className='w-full table-fixed'>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                className={
                  (header.column.columnDef.meta as { className?: string })
                    ?.className
                }
                key={header.id}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row, rowIndex) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell
                className={cn(
                  rowIndex % 2 !== 0 ? 'bg-card' : 'bg-card-lighter',
                  (cell.column.columnDef.meta as { className?: string })
                    ?.className,
                )}
                key={cell.id}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
